import { lazy, type ComponentType } from 'react';

/**
 * Every deploy renames the code-split chunks and Hosting drops the old ones, so a tab opened
 * before a deploy 404s on the next lazy page ("Failed to fetch dynamically imported module")
 * and React rendered NOTHING — devotionfund.com/churches showed a blank page (2026-10-27; same code here).
 * A stale chunk is fixed by one reload, which fetches the new index.html and its new chunks.
 */
// No vite:preloadError listener on purpose: preventing that event makes Vite resolve the import
// with `undefined`, React then throws "reading 'default'", and the heal also files a false crash
// report (caught by the client-error monitor on its first day, 2026-10-27). Letting Vite throw
// sends every case — missing JS or CSS — through lazyPage's catch below instead.
export const isChunkLoadError = (e: unknown) =>
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|ChunkLoadError|Loading chunk \d+ failed/i
    .test(String((e as Error)?.message ?? e));

const KEY = 'msf:chunk-reload-at';

/** Reload at most once a minute, so a genuinely missing file cannot loop forever. */
export function reloadOnceForNewDeploy(): boolean {
  try {
    const last = Number(sessionStorage.getItem(KEY) || 0);
    if (Date.now() - last < 60_000) return false;
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch { /* storage blocked: still reload once */ }
  window.location.reload();
  return true;
}

/** `lazy()` that heals a stale chunk with one reload instead of a blank page. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches React.lazy's own signature
export function lazyPage<T extends ComponentType<any>>(load: () => Promise<{ default: T }>) {
  return lazy(() => load().catch((e) => {
    if (isChunkLoadError(e) && reloadOnceForNewDeploy()) return new Promise<{ default: T }>(() => {});
    throw e; // AppErrorBoundary shows a reload screen and reports it
  }));
}
